import type { InfieldDef } from "../features/home/types";

export interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
}

import * as XLSX from "xlsx";
import Papa from "papaparse";
import type { ParseResult } from "papaparse";

export async function parseCsv(file: File): Promise<ParsedCsv> {
  return new Promise((resolve, reject) => {
    const isExcel = file.name.endsWith(".xlsx") || file.name.endsWith(".xls");
    
    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          const json: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          if (json.length === 0) throw new Error("Empty Excel file");
          
          const headers = (json[0] || []).map(h => String(h).trim());
          const rows: Record<string, string>[] = [];
          
          for (let i = 1; i < json.length; i++) {
            const values = json[i] || [];
            // Skip completely empty rows
            if (values.every(v => v === undefined || v === null || String(v).trim() === '')) continue;
            
            const row: Record<string, string> = {};
            headers.forEach((header, index) => {
              row[header] = values[index] !== undefined ? String(values[index]) : "";
            });
            rows.push(row);
          }
          
          resolve({ headers, rows });
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read Excel file"));
      reader.readAsArrayBuffer(file);
    } else {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results: ParseResult<Record<string, string>>) => {
          if (results.meta.fields) {
            const rows = results.data as Record<string, string>[];
            resolve({ headers: results.meta.fields, rows });
          } else {
            reject(new Error("No headers found in CSV"));
          }
        },
        error: (error: any) => {
          reject(error);
        }
      });
    }
  });
}

/**
 * Automaps agent infields to CSV headers by comparing lowercase versions.
 */
export function autoMap(infields: InfieldDef[], headers: string[]): Record<string, string> {
  const mapping: Record<string, string> = {};
  const lowerHeaders = headers.map(h => h.toLowerCase());

  for (const field of infields) {
    const fieldKey = field.key.toLowerCase();
    
    // Exact match first
    const exactIndex = lowerHeaders.indexOf(fieldKey);
    if (exactIndex !== -1) {
      mapping[field.key] = headers[exactIndex];
      continue;
    }
    
    // Partial match fallback (e.g. "first_name" matches "first name")
    const partialMatch = headers.find(h => 
      h.toLowerCase().replace(/[^a-z0-9]/g, '') === fieldKey.replace(/[^a-z0-9]/g, '')
    );
    
    if (partialMatch) {
      mapping[field.key] = partialMatch;
    }
  }

  return mapping;
}
